import React, { useContext } from "react";
import ExclamationCircleOutlined from "@ant-design/icons/ExclamationCircleOutlined";
import {
  StrategyOrder,
  SummaryData,
} from "@shared/features/bots/BotForm/common/types";
import { toFixed } from "@shared/utils/numbers";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import OrderNumber from "common/Numbering/OrderNumber";
import Popover from "common/Popover/Popover";
import Text from "common/Typography/Text";
import styled from "styled-components";

interface HedgingGridsSummaryProps {
  orders: SummaryData["orders"];
}

const HedgingGridsSummary = ({ orders }: HedgingGridsSummaryProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const { symbolPriceData, botAmountData } = useContext(BotFormContext)!;

  const getPriceDistance = (price: number) => {
    const currentPrice = symbolPriceData?.data;
    const pricePrecision = symbolPriceData?.precision.price || 0;
    const distance = currentPrice
      ? toFixed(((currentPrice - price) / currentPrice) * -100, 2)
      : 0;
    return `${toFixed(price, pricePrecision)} (${distance}%)`;
  };

  const getHedgingAmount = (
    ratio: number | undefined,
    order: StrategyOrder | undefined
  ) => {
    if (ratio === undefined) return "-";

    const content = (
      <HedgingAmount>
        <span>{toFixed(ratio, 2)}%</span>
        <span>({toFixed(order?.usdAmount || 0, 2)} USD)</span>
      </HedgingAmount>
    );

    if (order?.isValid !== false) return content;

    return (
      <InvalidHedgingAmount type="danger" as="div">
        {content}
        <Popover
          placement="topRight"
          content={`Min amount: ${order.minAmount} ${
            botAmountData?.mainCoin ?? ""
          }`}
        >
          <ExclamationCircleOutlined />
        </Popover>
      </InvalidHedgingAmount>
    );
  };

  return (
    <Wrapper>
      <Header>
        <Text type="secondary" as="div">
          {t("ordersSummary.columns.price_distance")}
        </Text>
        <Text type="secondary" as="div">
          {t("labels.long_hedging")}
        </Text>
        <Text type="secondary" as="div">
          {t("labels.short_hedging")}
        </Text>
      </Header>
      {orders?.map((order, index) => (
        <Row key={index}>
          <PriceCell>
            <OrderNumber displayNumber={index + 1} />
            <Text as="div">{getPriceDistance(order.price)}</Text>
          </PriceCell>
          <Cell>
            {getHedgingAmount(order.longHedgingRatio, order.longHedgingOrder)}
          </Cell>
          <Cell>
            {getHedgingAmount(order.shortHedgingRatio, order.shortHedgingOrder)}
          </Cell>
        </Row>
      ))}
    </Wrapper>
  );
};

export default HedgingGridsSummary;

const Wrapper = styled.div`
  min-width: 0;
`;
const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(112px, 1.15fr) minmax(78px, 0.925fr) minmax(
      78px,
      0.925fr
    );
  column-gap: 8px;
  align-items: center;
`;
const Header = styled(SummaryGrid)`
  background-color: ${(p) => p.theme.colors.titleCardBgColor};
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 12px;
`;
const Row = styled(SummaryGrid)`
  padding: 4px 12px;
`;
const PriceCell = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;

  > div:last-child {
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;
const Cell = styled.div`
  min-width: 0;
`;
const HedgingAmount = styled.span`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 4px;
  row-gap: 2px;
  min-width: 0;
  overflow-wrap: anywhere;
`;
const InvalidHedgingAmount = styled(Text)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  min-width: 0;
  width: 100%;

  .anticon {
    flex: 0 0 auto;
  }
`;
